import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseApi";
import {
  ApiResponse,
  StoreStockSummary,
  OutletStockSummary,
  StockComparison,
  StockDistribution,
  MultiOutletStockFilters,
} from "@/types";

export const multiOutletStockApi = createApi({
  reducerPath: "multiOutletStockApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["MultiOutletStock"],
  endpoints: builder => ({
    // Get stock summary for an item across all outlets in a store
    getStoreStockSummary: builder.query<
      ApiResponse<StoreStockSummary>,
      { storeId: string; itemId: string }
    >({
      query: ({ storeId, itemId }) => `/inventory/stores/${storeId}/items/${itemId}/stock-summary`,
      providesTags: (_, __, { storeId, itemId }) => [
        { type: "MultiOutletStock", id: `STORE_${storeId}_ITEM_${itemId}` },
      ],
    }),

    // Get all stock items for a specific outlet
    getOutletStockSummary: builder.query<
      ApiResponse<OutletStockSummary>,
      { outletId: string; filters?: MultiOutletStockFilters }
    >({
      query: ({ outletId, filters = {} }) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
          }
        });
        return `/inventory/outlets/${outletId}/stock-summary?${searchParams.toString()}`;
      },
      providesTags: (_, __, { outletId }) => [
        { type: "MultiOutletStock", id: `OUTLET_${outletId}` },
      ],
    }),

    // Get low stock alerts for an outlet
    getLowStockAlerts: builder.query<ApiResponse<OutletStockSummary>, string>({
      query: outletId => `/inventory/outlets/${outletId}/low-stock`,
      providesTags: (_, __, outletId) => [
        { type: "MultiOutletStock", id: `LOW_STOCK_${outletId}` },
      ],
    }),

    // Compare stock levels across all outlets for a store
    compareOutletStocks: builder.query<
      ApiResponse<StockComparison>,
      { storeId: string; filters?: { categoryId?: string; subcategoryId?: string } }
    >({
      query: ({ storeId, filters = {} }) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
          }
        });
        return `/inventory/stores/${storeId}/stock-comparison?${searchParams.toString()}`;
      },
      providesTags: (_, __, { storeId }) => [
        { type: "MultiOutletStock", id: `COMPARISON_${storeId}` },
      ],
    }),

    // Get stock distribution chart data
    getStockDistribution: builder.query<ApiResponse<StockDistribution>, string>({
      query: storeId => `/inventory/stores/${storeId}/stock-distribution`,
      providesTags: (_, __, storeId) => [
        { type: "MultiOutletStock", id: `DISTRIBUTION_${storeId}` },
      ],
    }),
  }),
});

export const {
  useGetStoreStockSummaryQuery,
  useGetOutletStockSummaryQuery,
  useGetLowStockAlertsQuery,
  useCompareOutletStocksQuery,
  useGetStockDistributionQuery,
} = multiOutletStockApi;
